// src/pages/stock/EsimBulkUpload.tsx

import { useCallback, useEffect, useMemo, useState } from 'react';
import type React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Chip,
  IconButton,
  TableCell,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import DragDropFile from '@components/common/DragDropFile';
import { usePageHeader } from '@components/common/pageHeader.context';

import { useUploadHistories } from '@queries/esimBulk/useUploadHistories';
import { useUploadHistoriesCount } from '@queries/esimBulk/useUploadHistoriesCount';

import {
  deleteUploadHistory,
  uploadFile,
  type GetUploadHistoriesResponse,
} from '@apis/esimBulk/upload';

import { ESIM_BULK_UPLOAD_TEMPLATE } from '@constants/templates';
import { getBulkRunStatusMeta } from '@constants/bulkLabels';

import { downloadFile } from '@utils/file';
import { timestampToDate } from '@utils/time';

import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

import {
  StandardPagination,
  StandardTableFrame,
  StandardTableToolbar,
  cellSx,
} from '@components/common/table';

const PAGE_SIZE = 50;

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

/**
 * eSIM Bulk Upload - History
 *
 * Responsibilities
 * - Upload Excel file and navigate to the created detail page.
 * - Render upload history with pagination.
 * - Provide run-level delete action (soft-disabled when isDeleted=true).
 * - Keep list/count in sync after mutations.
 * - Navigate ONLY via index cell (click / keyboard).
 */
export const EsimBulkUpload = () => {
  const navigate = useNavigate();
  const { setPageHeaderSlots } = usePageHeader();

  const confirm = useConfirm();
  const systemAlert = useSystemAlert();

  const [page, setPage] = useState(1);

  // History list and count (separate queries to mirror existing API surface).
  const {
    data: historiesData,
    refetch: refetchHistories,
    isLoading: isHistoriesLoading,
  } = useUploadHistories({ page });

  const {
    data: totalCount,
    refetch: refetchCount,
    isLoading: isCountLoading,
  } = useUploadHistoriesCount();

  const rows: GetUploadHistoriesResponse[] = historiesData?.data ?? [];

  const totalPages = useMemo(() => {
    const count = totalCount ?? 0;
    return Math.max(1, Math.ceil(count / PAGE_SIZE));
  }, [totalCount]);

  // When total pages shrink (e.g., after delete), keep current page in range.
  useEffect(() => {
    setPage((prev) => clamp(prev, 1, totalPages));
  }, [totalPages]);

  /** Refresh both list and count; errors are swallowed to avoid blocking UX. */
  const refreshHistories = useCallback(async () => {
    try {
      await Promise.all([refetchHistories(), refetchCount()]);
    } catch {
      /* noop */
    }
  }, [refetchCount, refetchHistories]);

  /** Upload flow: submit -> refresh -> go to detail. */
  const handleConfirmFile = useCallback(
    async (file: File) => {
      try {
        const { data, message } = await uploadFile(file);

        if (message !== 'OK') {
          systemAlert.error(message || 'Upload failed.');
          return;
        }

        const uploadId = Number(data?.[0]);
        if (!Number.isFinite(uploadId) || uploadId <= 0) {
          systemAlert.error('Upload succeeded but returned an invalid upload id.');
          return;
        }

        await refreshHistories();
        navigate(`/stocks/upload/${uploadId}`);
      } catch {
        systemAlert.error('Upload failed.');
      }
    },
    [navigate, refreshHistories, systemAlert],
  );

  /** Download the server-provided Excel template. */
  const handleDownloadTemplate = useCallback(() => {
    downloadFile(ESIM_BULK_UPLOAD_TEMPLATE, 'esim_bulk_upload_template');
    systemAlert.success('Template download started.');
  }, [systemAlert]);

  /** Delete a history run with confirmation. */
  const handleDeleteUpload = useCallback(
    async (uploadId: number) => {
      const ok = await confirm({
        priority: 'danger',
        title: 'Delete this history?',
        description: 'This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        autoFocusConfirm: false,
      });

      if (!ok) return;

      try {
        await deleteUploadHistory(uploadId);
        systemAlert.success('Deleted.');
      } catch {
        systemAlert.error('Failed to delete.');
      } finally {
        await refreshHistories();
      }
    },
    [confirm, refreshHistories, systemAlert],
  );

  /**
   * Register header actions.
   * PageHeaderProvider clears slots on route change; no manual cleanup required.
   */
  useEffect(() => {
    setPageHeaderSlots({
      actions: [
        {
          key: 'download-upload-template',
          label: 'Download Upload Template',
          startIcon: <UploadFileIcon />,
          onClick: handleDownloadTemplate,
          variant: 'outlined',
          primary: true,
        },
      ],
    });
  }, [handleDownloadTemplate, setPageHeaderSlots]);

  // Table columns: #, stockCode, stockName, filename, total, ok, fail, status, createdAt, delete
  const columnsCount = 10;

  const head = useMemo(
    () => (
      <TableRow>
        <TableCell align="right" sx={cellSx.nowrap}>#</TableCell>
        <TableCell sx={cellSx.nowrap}>Stock Code</TableCell>
        <TableCell sx={cellSx.nowrap}>Stock Name</TableCell>
        <TableCell sx={cellSx.nowrap}>File name</TableCell>
        <TableCell align="right" sx={cellSx.nowrap}>Total</TableCell>
        <TableCell align="right" sx={cellSx.nowrap}>OK</TableCell>
        <TableCell align="right" sx={cellSx.nowrap}>Fail</TableCell>
        <TableCell sx={cellSx.nowrap}>Status</TableCell>
        <TableCell sx={cellSx.nowrap}>Date &amp; Time</TableCell>
        <TableCell align="center" sx={cellSx.nowrap}>Delete</TableCell>
      </TableRow>
    ),
    [],
  );

  const isLoading = Boolean(isHistoriesLoading || isCountLoading);

  return (
    <>
      {/* Upload area */}
      <Box width="100%" mt={2}>
        <DragDropFile onConfirmFile={handleConfirmFile} />
      </Box>

      {/* Keep spacing consistent with legacy layout */}
      <Toolbar />

      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Upload History
      </Typography>

      <Box mt={2}>
        <StandardTableToolbar
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={totalCount ?? 0}
          onChangePage={(next) => setPage(clamp(next, 1, totalPages))}
        />

        <StandardTableFrame<GetUploadHistoriesResponse>
          head={head}
          rows={rows}
          getRowKey={(row) => row.esimInventoryBulkUploadId}
          columnsCount={columnsCount}
          isLoading={isLoading}
          loadingVariant="skeleton"
          emptyText="No upload history found."
          // Visual state: highlight as "inactive" when the run was deleted.
          getRowStatus={(row) => (row.isDeleted ? 'inactive' : 'normal')}
          renderRow={(history, index) => {
            const uploadId = history.esimInventoryBulkUploadId;
            const isDeleted = Boolean(history.isDeleted);
            const statusSpec = getBulkRunStatusMeta(history.status, { isDeleted });
            const hasFail = Number(history.failedCount ?? 0) > 0;

            const canNavigate = Number.isFinite(uploadId) && uploadId > 0;

            return (
              <>
                {/* Navigate ONLY via the index cell (click + keyboard). */}
                <TableCell align="right" sx={cellSx.number}>
                  <Box
                    component="span"
                    role={canNavigate ? 'button' : undefined}
                    tabIndex={canNavigate ? 0 : -1}
                    onClick={() => {
                      if (!canNavigate) return;
                      navigate(`/stocks/upload/${uploadId}`);
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLSpanElement>) => {
                      if (!canNavigate) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/stocks/upload/${uploadId}`);
                      }
                    }}
                    sx={{ cursor: canNavigate ? 'pointer' : 'default' }}
                  >
                    <Typography
                      variant="body2"
                      color={canNavigate ? 'primary' : 'text.primary'}
                      sx={{
                        display: 'inline',
                        textDecoration: canNavigate ? 'underline' : 'none',
                      }}
                    >
                      {(page - 1) * PAGE_SIZE + index + 1}
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell sx={cellSx.nowrap}>{history.stock?.stockCode}</TableCell>
                <TableCell sx={cellSx.nowrap}>{history.stock?.stockName}</TableCell>
                <TableCell sx={cellSx.nowrap}>
                  {history.fileUrl ? (
                    <Typography
                      component="a"
                      href={history.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      color="primary"
                      sx={{ textDecoration: 'underline' }}
                    >
                      {history.filename}
                    </Typography>
                  ) : (
                    history.filename
                  )}
                </TableCell>

                {/* Total: neutral */}
                <TableCell align="right" sx={cellSx.number}>
                  <Typography component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {history.totalCount}
                  </Typography>
                </TableCell>

                {/* OK: success emphasis */}
                <TableCell align="right" sx={cellSx.number}>
                  <Typography
                    component="span"
                    sx={{
                      color: 'success.main',
                      fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {history.okayCount}
                  </Typography>
                </TableCell>

                {/* Fail: emphasize only when > 0 */}
                <TableCell align="right" sx={cellSx.number}>
                  <Typography
                    component="span"
                    sx={{
                      color: hasFail ? 'error.main' : 'text.secondary',
                      fontWeight: hasFail ? 700 : 400,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {history.failedCount}
                  </Typography>
                </TableCell>

                {/* Status: user-friendly chip (Deleted overrides) */}
                <TableCell sx={cellSx.nowrap}>
                  <Chip
                    size="small"
                    label={statusSpec.label}
                    color={statusSpec.chip.color}
                    variant={statusSpec.chip.variant}
                    sx={{ fontWeight: 700 }}
                  />
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  {timestampToDate(history.createdAt)}
                </TableCell>

                {/* Delete: prevent accidental row navigation if row onClick is added later. */}
                <TableCell align="center" sx={cellSx.nowrap}>
                  <IconButton
                    size="small"
                    disabled={isDeleted}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDeleteUpload(uploadId);
                    }}
                    aria-label="delete upload history"
                  >
                    <DeleteOutline />
                  </IconButton>
                </TableCell>
              </>
            );
          }}
        />

        <Box mt={2}>
          <StandardPagination
            page={page}
            pageSize={PAGE_SIZE}
            totalCount={totalCount ?? 0}
            onChangePage={(next) => setPage(clamp(next, 1, totalPages))}
          />
        </Box>
      </Box>
    </>
  );
};
